import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getPreviewSignIds } from '@/utils/freeSigns';
import { ContentGateModal } from '@/components/ContentGate';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { BackButton } from '@/components/BackButton';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { trackSignView } from '@/utils/analytics';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  Ban, 
  Info, 
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Volume2,
  Flag,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/config';
import { getPrefetched, getResolvedData, hasPrefetch } from '@/utils/dataPrefetcher';

// Convert relative image URL (/api/uploads/...) to absolute URL for SEO (og:image, Schema.org)
const toAbsoluteImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `https://drivemaster.se${url}`;
  return `https://drivemaster.se/${url}`;
};

// Translation helper for all 9 languages
const texts = {
  lockRegTitle: { en: 'Unlock for free', sv: 'Lås upp gratis', ar: 'افتحها مجاناً', tr: 'Ücretsiz aç', fa: 'رایگان باز کنید', so: 'Si bilaash ah u fur', pl: 'Odblokuj za darmo', es: 'Desbloquéala gratis', de: 'Kostenlos freischalten' },
  lockRegDesc: { en: 'Sign up to read the full description of this sign.', sv: 'Registrera dig för att läsa hela beskrivningen av detta märke.', ar: 'سجّل لقراءة الوصف الكامل لهذه الإشارة.', tr: 'Bu işaretin tam açıklamasını okumak için kaydolun.', fa: 'برای خواندن توضیح کامل این علامت ثبت‌نام کنید.', so: 'Is diiwaangeli si aad u akhrido sharraxaadda buuxda ee calaamaddan.', pl: 'Zarejestruj się, aby przeczytać pełny opis tego znaku.', es: 'Regístrate para leer la descripción completa de esta señal.', de: 'Registriere dich, um die vollständige Beschreibung dieses Zeichens zu lesen.' },
  lockRegCta: { en: 'Sign up', sv: 'Registrera dig', ar: 'إنشاء حساب', tr: 'Kaydol', fa: 'ثبت‌نام', so: 'Is diiwaangeli', pl: 'Zarejestruj się', es: 'Registrarse', de: 'Registrieren' },
  lockPlanTitle: { en: 'Unlock all traffic signs', sv: 'Lås upp alla trafikmärken', ar: 'افتح جميع إشارات المرور', tr: 'Tüm trafik işaretlerini aç', fa: 'همه علائم راهنمایی را باز کنید', so: 'Fur dhammaan calaamadaha taraafikada', pl: 'Odblokuj wszystkie znaki drogowe', es: 'Desbloquea todas las señales de tráfico', de: 'Alle Verkehrszeichen freischalten' },
  lockPlanDesc: { en: 'Get full descriptions of every traffic sign with a subscription.', sv: 'Få fullständiga beskrivningar av alla trafikmärken med ett abonnemang.', ar: 'احصل على الوصف الكامل لكل إشارات المرور مع الاشتراك.', tr: 'Abonelikle tüm trafik işaretlerinin tam açıklamalarını alın.', fa: 'با اشتراک توضیح کامل همه علائم راهنمایی را دریافت کنید.', so: 'Ku hel sharraxaad buuxda oo calaamad kasta ah adigoo isdiiwaangeliya.', pl: 'Pełne opisy wszystkich znaków drogowych w subskrypcji.', es: 'Consigue la descripción completa de todas las señales con una suscripción.', de: 'Mit einem Abo erhältst du vollständige Beschreibungen aller Verkehrszeichen.' },
  lockPlanCta: { en: 'See plans', sv: 'Se paket', ar: 'عرض الباقات', tr: 'Paketleri gör', fa: 'مشاهده بسته‌ها', so: 'Arag qorshayaasha', pl: 'Zobacz pakiety', es: 'Ver planes', de: 'Pakete ansehen' },
  loading: { en: 'Loading...', ar: 'جاري التحميل...', sv: 'Laddar...', tr: 'Yükleniyor...', fa: 'در حال بارگذاری...', so: 'Soo dejinaya...', pl: 'Ładowanie...', es: 'Cargando...', de: 'Laden...' },
  signNotFound: { en: 'Sign Not Found', ar: 'الإشارة غير موجودة', sv: 'Skylten hittades inte', tr: 'İşaret Bulunamadı', fa: 'علامت یافت نشد', so: 'Calaamadda lama helin', pl: 'Znak nie znaleziony', es: 'Señal no encontrada', de: 'Zeichen nicht gefunden' },
  backToSigns: { en: 'Back to Signs', ar: 'العودة للإشارات', sv: 'Tillbaka till skyltar', tr: 'İşaretlere Dön', fa: 'بازگشت به علائم', so: 'Ku noqo Calaamadaha', pl: 'Powrót do znaków', es: 'Volver a las señales', de: 'Zurück zu den Zeichen' },
  description: { en: 'Description', ar: 'الوصف', sv: 'Beskrivning', tr: 'Açıklama', fa: 'توضیحات', so: 'Sharaxaad', pl: 'Opis', es: 'Descripción', de: 'Beschreibung' },
  noDescription: { en: 'No description available', ar: 'لا يوجد وصف متاح', sv: 'Ingen beskrivning tillgänglig', tr: 'Açıklama mevcut değil', fa: 'توضیحی موجود نیست', so: 'Sharaxaad ma jirto', pl: 'Brak opisu', es: 'Sin descripción disponible', de: 'Keine Beschreibung verfügbar' },
  relatedSigns: { en: '🚦 Related Signs', ar: '🚦 إشارات مشابهة', sv: '🚦 Liknande skyltar', tr: '🚦 İlgili İşaretler', fa: '🚦 علائم مرتبط', so: '🚦 Calaamadaha la xidhiidha', pl: '🚦 Powiązane znaki', es: '🚦 Señales relacionadas', de: '🚦 Verwandte Zeichen' },
  warningSign: { en: 'Warning Sign', ar: 'إشارة تحذيرية', sv: 'Varningsskylt', tr: 'Uyarı İşareti', fa: 'علامت هشدار', so: 'Calaamad Digniin', pl: 'Znak ostrzegawczy', es: 'Señal de advertencia', de: 'Warnzeichen' },
  prohibitionSign: { en: 'Prohibition Sign', ar: 'إشارة منع', sv: 'Förbudsskylt', tr: 'Yasak İşareti', fa: 'علامت ممنوعیت', so: 'Calaamad Mamnuuc', pl: 'Znak zakazu', es: 'Señal de prohibición', de: 'Verbotszeichen' },
  mandatorySign: { en: 'Mandatory Sign', ar: 'إشارة إلزامية', sv: 'Påbudsskylt', tr: 'Zorunlu İşaret', fa: 'علامت اجباری', so: 'Calaamad Waajib', pl: 'Znak nakazu', es: 'Señal obligatoria', de: 'Gebotszeichen' },
  informativeSign: { en: 'Informative Sign', ar: 'إشارة إرشادية', sv: 'Informationsskylt', tr: 'Bilgi İşareti', fa: 'علامت اطلاعاتی', so: 'Calaamad Macluumaad', pl: 'Znak informacyjny', es: 'Señal informativa', de: 'Hinweiszeichen' },
  details: { en: 'Details', ar: 'التفاصيل', sv: 'Detaljer', tr: 'Detaylar', fa: 'جزئیات', so: 'Faahfaahin', pl: 'Szczegóły', es: 'Detalles', de: 'Details' },
  speed: { en: 'Speed', ar: 'السرعة', sv: 'Hastighet', tr: 'Hız', fa: 'سرعت', so: 'Xawaaraha', pl: 'Prędkość', es: 'Velocidad', de: 'Geschwindigkeit' },
  reportError: { en: 'Report Error', sv: 'Rapportera fel', ar: 'الإبلاغ عن خطأ', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Soo sheeg khalad', pl: 'Zgłoś błąd', es: 'Reportar error', de: 'Fehler melden' },
  reportTitle: { en: 'Report Error', sv: 'Rapportera fel', ar: 'الإبلاغ عن خطأ', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Soo sheeg khalad', pl: 'Zgłoś błąd', es: 'Reportar error', de: 'Fehler melden' },
  helpUs: { en: 'Help us improve', sv: 'Hjälp oss förbättra', ar: 'ساعدنا في تحسين المحتوى', tr: 'Geliştirmemize yardımcı olun', fa: 'به ما کمک کنید', so: 'Naga caawi', pl: 'Pomóż nam', es: 'Ayúdanos a mejorar', de: 'Helfen Sie uns' },
  errorType: { en: 'Error Type', sv: 'Feltyp', ar: 'نوع الخطأ', tr: 'Hata Türü', fa: 'نوع خطا', so: 'Nooca khaladka', pl: 'Typ błędu', es: 'Tipo de error', de: 'Fehlertyp' },
  additionalDetails: { en: 'Additional Details', sv: 'Ytterligare detaljer', ar: 'تفاصيل إضافية', tr: 'Ek detaylar', fa: 'جزئیات اضافی', so: 'Faahfaahin dheeraad', pl: 'Dodatkowe szczegóły', es: 'Detalles adicionales', de: 'Zusätzliche Details' },
  submitReport: { en: 'Submit', sv: 'Skicka', ar: 'إرسال', tr: 'Gönder', fa: 'ارسال', so: 'Dir', pl: 'Wyślij', es: 'Enviar', de: 'Senden' },
  cancel: { en: 'Cancel', sv: 'Avbryt', ar: 'إلغاء', tr: 'İptal', fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' },
  selectError: { en: 'Select error type', sv: 'Välj feltyp', ar: 'اختر نوع الخطأ', tr: 'Hata türünü seçin', fa: 'نوع خطا را انتخاب کنید', so: 'Dooro nooca', pl: 'Wybierz typ', es: 'Selecciona el tipo', de: 'Typ auswählen' },
  reportSuccess: { en: 'Report sent!', sv: 'Rapport skickad!', ar: 'تم الإرسال!', tr: 'Gönderildi!', fa: 'ارسال شد!', so: 'La diray!', pl: 'Wysłano!', es: '¡Enviado!', de: 'Gesendet!' },
  reportFailed: { en: 'Error sending', sv: 'Fel vid skickande', ar: 'خطأ في الإرسال', tr: 'Gönderme hatası', fa: 'خطا در ارسال', so: 'Khalad', pl: 'Błąd', es: 'Error', de: 'Fehler' },
};

// أنواع الأخطاء
const reportTypes = [
  { value: 'translation_error', label: { ar: 'خطأ في الترجمة', sv: 'Översättningsfel', en: 'Translation error', tr: 'Çeviri hatası', fa: 'خطای ترجمه', so: 'Khalad turjumaad', pl: 'Błąd tłumaczenia', es: 'Error de traducción', de: 'Übersetzungsfehler' } },
  { value: 'wrong_info', label: { ar: 'معلومات خاطئة', sv: 'Fel information', en: 'Wrong information', tr: 'Yanlış bilgi', fa: 'اطلاعات اشتباه', so: 'Macluumaad khaldan', pl: 'Błędne informacje', es: 'Información incorrecta', de: 'Falsche Information' } },
  { value: 'missing_image', label: { ar: 'صورة مفقودة', sv: 'Bild saknas', en: 'Missing image', tr: 'Eksik resim', fa: 'تصویر گمشده', so: 'Sawir maqan', pl: 'Brakujący obraz', es: 'Imagen faltante', de: 'Fehlendes Bild' } },
  { value: 'other', label: { ar: 'خطأ آخر', sv: 'Annat fel', en: 'Other', tr: 'Diğer', fa: 'سایر', so: 'Kale', pl: 'Inne', es: 'Otro', de: 'Sonstiges' } }
];

const getText = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const TrafficSignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const passedSign = location.state?.signData || null;
  
  const findSignInList = () => {
    const listData = getResolvedData('signs');
    if (listData && Array.isArray(listData)) {
      return listData.find(s => s.id === id || (typeof s.name === 'string' ? s.name : (s.name?.sv || '')).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') === id) || null;
    }
    return null;
  };
  
  const [sign, setSign] = useState(() => getResolvedData(`sign_${id}`) || passedSign || findSignInList());
  const [allSigns, setAllSigns] = useState([]);
  const [loading, setLoading] = useState(() => {
    if (getResolvedData(`sign_${id}`) || passedSign || findSignInList()) return false;
    if (hasPrefetch(`sign_${id}`)) return false;
    return true;
  });
  const [error, setError] = useState(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'fa';
  const lang = i18n.language;

  const isSubscribed = user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';

  // Get category label with 9 language support
  const getCategoryLabel = (category) => {
    const categoryLabels = {
      warning: texts.warningSign,
      prohibition: texts.prohibitionSign,
      mandatory: texts.mandatorySign,
      informative: texts.informativeSign
    };
    return getText(category, lang) || categoryLabels[category]?.[lang] || categoryLabels[category]?.en || category;
  };

  const categories = {
    warning: { 
      labelKey: 'warningSign',
      icon: AlertTriangle,
      color: 'from-yellow-500 to-amber-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-200'
    },
    prohibition: { 
      labelKey: 'prohibitionSign',
      icon: Ban,
      color: 'from-red-500 to-rose-500',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      borderColor: 'border-red-200'
    },
    mandatory: { 
      labelKey: 'mandatorySign',
      icon: CheckCircle,
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200'
    },
    informative: { 
      labelKey: 'informativeSign',
      icon: Info,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    }
  };

  useEffect(() => {
    fetchSignDetails();
    fetchAllSigns();
    trackSignView(id);
  }, [id]);

  // تحديد الإشارات المجانية الثابتة (نفس المنطق في صفحة القائمة)
  const getFreeSignIds = (signs) => getPreviewSignIds(signs, user ? 3 : 2);

  // Check if current sign is free (by ID or slug match)
  const [isPaidSign, setIsPaidSign] = useState(false);
  const [gateMode, setGateMode] = useState(null);
  const needsRegister = !user && !!sign && getPreviewSignIds(allSigns, 3).includes(sign.id);
  
  useEffect(() => {
    if (allSigns.length > 0 && sign && !isSubscribed) {
      const freeIds = getFreeSignIds(allSigns);
      const signId = sign.id;
      setIsPaidSign(!freeIds.includes(signId));
    } else {
      setIsPaidSign(false);
    }
  }, [allSigns, sign, isSubscribed]);

  const generateSlugFromName = (name) => {
    if (!name) return '';
    const sv = typeof name === 'string' ? name : (name.sv || name.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const fetchSignDetails = async () => {
    const hasInitialData = !!sign;
    // 1. Try hover-based prefetch
    const prefetched = await getPrefetched(`sign_${id}`);
    if (prefetched) {
      setSign(prefetched);
      const localName = typeof prefetched.name === 'object' ? (prefetched.name?.[lang] || prefetched.name?.sv || '') : prefetched.name;
      const localDesc = typeof prefetched.description === 'object' ? (prefetched.description?.[lang] || prefetched.description?.sv || '') : prefetched.description;
      document.title = `${localName} - DriveMaster`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `${localName}: ${localDesc?.substring(0, 150)}`);
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage && prefetched.image_url) ogImage.setAttribute('content', toAbsoluteImageUrl(prefetched.image_url));
      setLoading(false);
      return;
    }
    // 2. Try finding in prefetched list data (by ID or by slug)
    const allSignsData = await getPrefetched('signs');
    if (allSignsData && Array.isArray(allSignsData)) {
      const found = allSignsData.find(s => s.id === id || generateSlugFromName(s.name) === id);
      if (found) {
        setSign(found);
        setLoading(false);
        // Still fetch full details (with description) in background
        try {
          let response;
          try { response = await axios.get(`${API}/traffic-signs/${found.id || id}`); }
          catch { response = await axios.get(`${API}/traffic-signs/by-slug/${id}`); }
          if (response.data) setSign(response.data);
        } catch {}
        return;
      }
    }
    // 3. Fallback to API
    if (!hasInitialData) setLoading(true);
    try {
      let response;
      try {
        response = await axios.get(`${API}/traffic-signs/${id}`);
      } catch {
        response = await axios.get(`${API}/traffic-signs/by-slug/${id}`);
      }
      const signData = response.data;
      setSign(signData);
      
      const localName = typeof signData.name === 'object' ? (signData.name?.[lang] || signData.name?.sv || '') : signData.name;
      const localDesc = typeof signData.description === 'object' ? (signData.description?.[lang] || signData.description?.sv || '') : signData.description;
      document.title = `${localName} - DriveMaster`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `${localName}: ${localDesc?.substring(0, 150)}`);
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage && signData.image_url) ogImage.setAttribute('content', toAbsoluteImageUrl(signData.image_url));
    } catch (error) {
      console.error('Error fetching sign:', error);
      if (!hasInitialData) setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSigns = async () => {
    try {
      const prefetched = await getPrefetched('signs');
      if (prefetched) {
        setAllSigns(prefetched);
      } else {
        const response = await axios.get(`${API}/traffic-signs?lang=${i18n.language || 'sv'}`);
        setAllSigns(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching all signs:', error);
    }
  };

  // Navigation to next/previous sign
  const getCurrentIndex = () => {
    if (!sign) return -1;
    // Match by actual sign ID (not URL slug)
    const idx = allSigns.findIndex(s => s.id === sign.id);
    if (idx !== -1) return idx;
    // Fallback: match by slug
    return allSigns.findIndex(s => generateSlug(s.name) === id);
  };
  
  const canAccessSignById = (signId) => {
    // المشتركين يمكنهم الوصول لجميع الإشارات
    if (isSubscribed) return true;
    // غير المشتركين يمكنهم فقط الوصول للإشارات المجانية
    const freeIds = getFreeSignIds(allSigns);
    return freeIds.includes(signId);
  };
  
  // Helper to safely get sign name
  const getSignName = (signObj, language = lang) => {
    if (!signObj?.name) return 'Traffic Sign';
    if (typeof signObj.name === 'string') return signObj.name;
    return signObj.name[language] || signObj.name.sv || signObj.name.en || 'Traffic Sign';
  };

  // Helper to safely get sign description
  const getSignDescription = (signObj, language = lang) => {
    if (!signObj?.description) return '';
    if (typeof signObj.description === 'string') return signObj.description;
    return signObj.description[language] || signObj.description.sv || signObj.description.en || '';
  };

  // Generate slug from Swedish name
  const generateSlug = (name) => {
    if (!name) return '';
    const sv = typeof name === 'string' ? name : (name.sv || name.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  // Get the correct localized path for a sign (uses ID for non-Latin languages)
  const getSignNavPath = (signObj) => {
    const langPaths = {
      sv: '/trafikmarken',
      ar: '/ar/isharat-almroor',
      en: '/en/traffic-signs',
      tr: '/tr/trafik-isaretleri',
      fa: '/fa/alamat-rahnamaii',
      so: '/so/calaamadaha-taraafikada',
      de: '/de/verkehrszeichen',
      es: '/es/senales-trafico',
      pl: '/pl/znaki-drogowe',
    };
    const basePath = langPaths[lang] || langPaths.sv;
    // Use sign ID directly - works for all languages
    return `${basePath}/${signObj.id}`;
  };

  // SEO-friendly path using slug (for canonical URLs and structured data)
  const getSignDetailPath = (signName) => {
    const slug = generateSlug(signName);
    const basePath = '/trafikmarken';
    return `${basePath}/${slug || 'sign'}`;
  };

  const goToNextSign = () => {
    const currentIndex = getCurrentIndex();
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < allSigns.length) {
      const nextSign = allSigns[nextIndex];
      if (canAccessSignById(nextSign.id)) {
        navigate(getSignNavPath(nextSign));
      } else {
        navigate('/pricing');
      }
    }
  };

  const goToPreviousSign = () => {
    const currentIndex = getCurrentIndex();
    const prevIndex = currentIndex - 1;
    
    if (prevIndex >= 0) {
      const prevSign = allSigns[prevIndex];
      if (canAccessSignById(prevSign.id)) {
        navigate(getSignNavPath(prevSign));
      } else {
        navigate('/pricing');
      }
    }
  };

  // Get related signs (same category)
  const getRelatedSigns = () => {
    if (!sign) return [];
    return allSigns
      .filter(s => s.category === sign.category && s.id !== sign.id)
      .slice(0, 4);
  };

  if ((loading && !sign) || (!sign && !error)) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100" dir={isRTL ? 'rtl' : 'ltr'}>
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8 mt-20">
          <Card className="p-12 text-center max-w-md mx-auto">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {getText('signNotFound', lang)}
            </h2>
            <Button onClick={() => navigate('/trafikmarken')} className="mt-4">
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              {getText('backToSigns', lang)}
            </Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories[sign.category] || categories.informative;
  const CategoryIcon = categoryInfo.icon;
  const currentIndex = getCurrentIndex();
  const relatedSigns = getRelatedSigns();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-8 mt-20">
        <BackButton to="/trafikmarken" className="mb-6" />

        {/* SEO Structured Data - Language-aware */}
        <Helmet>
          <title>{getSignName(sign)} - {getText(categories[sign.category]?.labelKey || 'informativeSign', lang)} | DriveMaster</title>
          <meta name="description" content={`${getSignName(sign)}: ${getSignDescription(sign).substring(0, 100)} - DriveMaster`} />
          <meta property="og:title" content={`${getSignName(sign)} - ${getText(categories[sign.category]?.labelKey || 'informativeSign', lang)} | DriveMaster`} />
          <meta property="og:description" content={`${getSignName(sign)}: ${getSignDescription(sign).substring(0, 120)}`} />
          <meta property="og:image" content={toAbsoluteImageUrl(sign.image_url)} />
          <meta property="og:image:alt" content={getSignName(sign)} />
          <meta property="og:image:width" content="400" />
          <meta property="og:image:height" content="400" />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="DriveMaster" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${getSignName(sign)} | DriveMaster`} />
          <meta name="twitter:image" content={toAbsoluteImageUrl(sign.image_url)} />
          <meta name="twitter:image:alt" content={getSignName(sign)} />
          <link rel="canonical" href={`https://drivemaster.se${getSignDetailPath(sign.name)}`} />
          {Object.entries({
            sv: 'trafikmarken', ar: 'ar/isharat-almroor', en: 'en/traffic-signs',
            tr: 'tr/trafik-isaretleri', fa: 'fa/alamat-rahnamaii', so: 'so/calaamadaha-taraafikada',
            de: 'de/verkehrszeichen', es: 'es/senales-trafico', pl: 'pl/znaki-drogowe'
          }).map(([hLang, hPath]) => (
            <link key={hLang} rel="alternate" hrefLang={hLang} href={`https://drivemaster.se/${hPath}/${generateSlug(sign.name)}`} />
          ))}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageObject",
            "name": getSignName(sign),
            "description": getSignDescription(sign),
            "contentUrl": toAbsoluteImageUrl(sign.image_url),
            "thumbnailUrl": toAbsoluteImageUrl(sign.image_url),
            "url": `https://drivemaster.se${getSignDetailPath(sign.name)}`,
            "creator": { "@type": "Organization", "name": "DriveMaster", "url": "https://drivemaster.se" },
            "copyrightHolder": { "@type": "Organization", "name": "Transportstyrelsen" },
            "license": "https://creativecommons.org/licenses/by/4.0/",
            "acquireLicensePage": `https://drivemaster.se${getSignDetailPath(sign.name)}#license`,
            "copyrightNotice": "Transportstyrelsen / DriveMaster",
            "creditText": "Transportstyrelsen - Swedish Transport Agency",
            "inLanguage": lang,
            "keywords": `${getSignName(sign)}, ${getText(categories[sign.category]?.labelKey || 'informativeSign', lang)}, ${lang === 'ar' ? 'إشارات المرور, رخصة القيادة, السويد' : lang === 'sv' ? 'trafikmärke, körkort, Sverige' : 'traffic signs, driving license, Sweden'}`
          })}</script>
        </Helmet>

        {/* Main Content Card */}
        <Card className={`overflow-hidden border-2 ${categoryInfo.borderColor} shadow-xl max-w-4xl mx-auto`}>
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${categoryInfo.color} p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <CategoryIcon className="w-6 h-6" />
                </div>
                <div>
                  <Badge className="bg-white/20 text-white border-0 mb-1">
                    {getText(categoryInfo.labelKey, lang)}
                  </Badge>
                  <p className="text-white/80 text-sm">
                    {lang === 'ar' ? `${currentIndex + 1} من ${allSigns.length}` : `${currentIndex + 1} / ${allSigns.length}`}
                  </p>
                </div>
              </div>
              
              {/* Navigation arrows */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPreviousSign}
                  disabled={currentIndex <= 0}
                  className="bg-white/20 hover:bg-white/30 text-white disabled:opacity-50"
                  data-testid="prev-sign-btn"
                >
                  {isRTL ? <ArrowRight className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNextSign}
                  disabled={currentIndex >= allSigns.length - 1}
                  className="bg-white/20 hover:bg-white/30 text-white disabled:opacity-50"
                  data-testid="next-sign-btn"
                >
                  {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Image and Content */}
          <div className="grid md:grid-cols-2 gap-0">
            {/* Image Section */}
            <div className={`${categoryInfo.bgColor} p-8 flex items-center justify-center`}>
              <div className="relative">
                <img
                  src={sign.image_url?.startsWith('http') ? sign.image_url : `${sign.image_url}?v=3`}
                  alt={getSignName(sign)}
                  className="w-64 h-64 object-contain drop-shadow-2xl"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f1f5f9" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="60"%3E🚦%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 bg-white">
              <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {getSignName(sign)}
              </h1>
              
              {isPaidSign ? (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6 text-center">
                  <div className="text-4xl mb-3">🔒</div>
                  <h3 className="font-bold text-lg text-slate-900 mb-2" data-testid="sign-lock-title">
                    {getText(needsRegister ? 'lockRegTitle' : 'lockPlanTitle', lang)}
                  </h3>
                  <p className="text-slate-600 mb-4 text-sm">
                    {getText(needsRegister ? 'lockRegDesc' : 'lockPlanDesc', lang)}
                  </p>
                  <Button onClick={() => setGateMode(needsRegister ? 'register' : 'plans')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sign-lock-cta">
                    {getText(needsRegister ? 'lockRegCta' : 'lockPlanCta', lang)}
                  </Button>
                  <ContentGateModal mode={gateMode} onClose={() => setGateMode(null)} lang={lang} />
                </div>
              ) : (
                <div className={`${categoryInfo.bgColor} ${categoryInfo.textColor} rounded-xl p-6`}>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    {getText('description', lang)}
                  </h3>
                  <p className="leading-relaxed text-lg">
                    {getSignDescription(sign) || getText('noDescription', lang)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Related Signs */}
        {relatedSigns.length > 0 && (
          <div className="mt-12 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">
              {getText('relatedSigns', lang)}
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedSigns.map((relatedSign) => {
                const canAccessRelated = canAccessSignById(relatedSign.id);
                
                return (
                  <Card
                    key={relatedSign.id}
                    className={`cursor-pointer hover:shadow-lg transition-all hover:scale-105 overflow-hidden ${!canAccessRelated ? 'opacity-60' : ''}`}
                    onClick={() => {
                      if (canAccessRelated) {
                        navigate(getSignNavPath(relatedSign));
                      } else {
                        navigate('/pricing');
                      }
                    }}
                  >
                    <div className={`${categoryInfo.bgColor} p-4 flex items-center justify-center relative`}>
                      <img
                        src={relatedSign.image_url?.startsWith('http') ? relatedSign.image_url : `${relatedSign.image_url}?v=3`}
                        alt={getSignName(relatedSign)}
                        className="w-20 h-20 object-contain"
                      />
                      {!canAccessRelated && (
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <span className="text-white text-2xl">🔒</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-sm text-slate-900 line-clamp-2">
                        {getSignName(relatedSign)}
                      </p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TrafficSignDetailPage;
